/**
 * Copyright (c) 2010-2022 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Injectable, Injector, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot } from '@angular/router';
import { combineLatest, Observable, of, zip } from 'rxjs';
import { AuthState } from '../../security/state/auth.state';
import { Store } from '@ngxs/store';
import { concatMap, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { LoggerService } from '../../../modules/shared/services/logger.service';
import { LoginMethodType, RequestedRoute } from '../../security/state/auth.model';
import { RedirectToLogin } from '../../security/state/auth.actions';
import { DashGisUtil } from '../../../modules/shared/dash-gis-util';
import { IsAuthenticatedTokens } from '../../security/is-authenticated-tokens';
import { IsAuthenticated } from '../../security/services/isAuthenticated';

@Injectable()
export class AuthenticatedGuard {
    excludedRoutes$: Observable<string[]> = inject(Store).select(AuthState.excludedRoutes);
    enabledMethods$: Observable<string[]> = inject(Store).select(AuthState.loginMethods);
    activeMethod$: Observable<LoginMethodType> = inject(Store).select(AuthState.activeMethod);
    private token$: Observable<string> = inject(Store).select(AuthState.token);
    private authenticationInProgress$: Observable<boolean> = inject(Store).select(AuthState.authenticationInProgress);
    private isStartingUp$: Observable<boolean> = inject(Store).select(AuthState.isStartingUp);
    private validated$: Observable<boolean> = inject(Store).select(AuthState.validated);

    constructor(
        private logger: LoggerService,
        private router: Router,
        private store: Store,
        private injector: Injector
    ) {}

    canLoad(route: Route): Observable<boolean> {
        const targetUrl = this.router.currentNavigation().extractedUrl.toString();
        const requested = DashGisUtil.buildRequestedRoute(targetUrl);
        return this.checkAuthenticatedState(requested);
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        const requested = DashGisUtil.buildRequestedRoute(state.url);
        return this.checkAuthenticatedState(requested);
    }

    private checkAuthenticatedState(requested: RequestedRoute) {
        return combineLatest([this.excludedRoutes$, this.authenticationInProgress$, this.isStartingUp$]).pipe(
            //  filter out if login / token validation is in progress
            filter(([, inProgress, startingUp]) => !inProgress && !startingUp),

            // get both the list of enabled methods, which we will use later, and the active method
            concatMap(([excludedRoutes, ,]) => {
                if (excludedRoutes.includes(requested.url)) {
                    return of(true);
                }
                return zip(this.enabledMethods$, this.activeMethod$).pipe(
                    // if there is an active method, only check that, otherwise check all enabled methods
                    map(([methods, activeMethod]) => (activeMethod === 'none' ? methods : [activeMethod])),

                    // switch path depending on result
                    switchMap((methods) => {
                        if (methods && methods.length > 0) {
                            return this.checkAuthenticatedServices$(methods);
                        } else {
                            return of(false);
                        }
                    }),

                    // if not authenticated, head to the login page
                    tap((canActivate) => {
                        if (!canActivate) {
                            setTimeout(() => this.store.dispatch(new RedirectToLogin(requested)), 0);
                        }
                    })
                );
            })
        );
    }

    private checkAuthenticatedServices$(validMethods: string[]) {
        return of(validMethods).pipe(
            // check valid methods
            mergeMap((methods) => {
                const authStreams = methods.map((method) => {
                    const service: IsAuthenticated = this.injector.get(IsAuthenticatedTokens.get(method));
                    return service.csIsAuthenticated$();
                });
                return zip(...authStreams);
            }),

            // any one successful method is sufficient
            concatMap((authenticated) => {
                // do not pass until validated
                if (authenticated.some((a) => a)) {
                    return this.validated$.pipe(filter((v) => v));
                }
                return of(false);
            })
        );
    }
}
